import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';

import { GetIssues, GetIssuesWithHeadlines } from '@root/modules/issue/types/issue';
import getIssuesWithHeadlines from '@root/modules/issue/graphql/getIssuesWithHeadlines.graphql';

import { getRouteParams } from '@root/common/utils/url';

type ServiceVariables = { id?: number | string; domain?: string };

type FetchVariables = GetIssues['variables'];

type Response = GetIssuesWithHeadlines['data'];

export default class IssueWithHeadlines extends Service implements ServiceInterface {
  private fixVariables({ id, domain }: ServiceVariables) {
    const params = getRouteParams({ id, desiredIdType: 'number' });
    const variables: FetchVariables = {
      id: params.id ? Number(params.id) : undefined,
      domain: !params.id ? domain : undefined,
      limit: 1,
    };
    return variables;
  }

  private async fetchIssueWithHeadlines(variables: FetchVariables) {
    const apiProvider = this.createProvider('GraphQL');
    apiProvider
      .selectAPI('content-api-v3')
      .setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true })
      .setCacheOptions({ key: `fetchIssueWithHeadlines:${JSON.stringify(variables)}`, ttl: 30 })
      .enableCache(true);

    const options = Object.assign({ query: getIssuesWithHeadlines }, { variables });
    const response = await apiProvider.request<Response>(options);

    if (!(response.issues.data.length > 0)) {
      this.handleError();
    }

    const issue = response.issues.data[0];
    const headlinesResponse = { items: response.issues.data[0].headlines.items.map(item => item.article) };

    return {
      issue,
      headlines: headlinesResponse,
    };
  }

  public handleError() {
    throw {
      message: 'issue.error.not_found',
      statusCode: 404,
    };
  }

  public async fetch(variables: ServiceVariables) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      this.handleError();
    }

    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchIssueWithHeadlines(fixedVariables);

    return response;
  }
}

export function fetchIssueWithHeadlines(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const issueWithHeadlines = new IssueWithHeadlines({ req });
  return issueWithHeadlines.fetch(variables);
}
